import { postFormData, request } from "../helper/axios_helper";
import { isSuccess, isTerminal } from "../helper/run_status";
import FormData from "form-data";

// ─── Job polling ──────────────────────────────────────────────
// Polls /job/{jobId}/status every 3 seconds until the job reaches ANY final status.
// Resolves when it succeeded, rejects otherwise.
//
// This used to test three exact strings — "Completed", "Completed with Error", "Error" — and keep
// polling on anything else. A master suite can also end "Timed Out" (its end-time cutoff fired) or
// "Stopped", and legacy rows carry "Complete" without the d. In those cases the promise NEVER
// settled: the caller's success/failure toast never fired and this 3-second interval ran for the
// life of the page. isTerminal/isSuccess own the vocabulary now, so a new status can be added in
// one place instead of leaking another silent hang.
export const pollJobStatus = (jobId, onStatusChange) => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const res = await request("GET", `/api/auth/job/${jobId}/status`);
        const status = res.data.jobStatus;

        if (onStatusChange) onStatusChange(status);

        if (isTerminal(status)) {
          clearInterval(interval);
          if (isSuccess(status)) resolve(res.data);
          else reject(res.data);
        }
      } catch (err) {
        // Don't stop polling on network errors — tab may have been suspended
        // Just log and keep trying
        console.warn("Polling interrupted, retrying...", err.message);
      }
    }, 3000);
  });
};

// ─── Compare endpoints — now return 202 immediately ──────────
export const fileToFileCompare = (
  jobId,
  category,
  subCategory,
  taskName,
  jobType,
  testFileOne,
  testFileTwo,
  delimiterOne,
  delimiterTwo,
  mapping,
  rowTests,
  fieldTests,
  mappingData,
) => {
  const formData = new FormData();
  formData.append("jobId", jobId);
  formData.append("category", category);
  formData.append("subCategory", subCategory);
  formData.append("taskName", taskName);
  formData.append("jobType", jobType);
  formData.append("testFileOne", testFileOne);
  formData.append("testFileTwo", testFileTwo);
  formData.append("delimiterOne", delimiterOne);
  formData.append("delimiterTwo", delimiterTwo);
  formData.append("mapping", mapping);
  formData.append("rowTests", rowTests);
  formData.append("fieldTests", fieldTests);
  formData.append("mappingTableData", JSON.stringify(mappingData));
  return postFormData("/api/auth/compare/filetofile", formData);
};

export const fileToDbCompareWithMultiPart = (
  jobId,
  suiteId,
  file,
  category,
  subCategory,
  taskName,
  jobType,
  sql,
  dbName,
  mapping,
  rowTests,
  fieldTests,
  mappingData,
  delimiter,
) => {
  const formData = new FormData();
  formData.append("inputFile", file);
  formData.append("jobId", jobId);
  formData.append("suiteId", suiteId);
  formData.append("category", category);
  formData.append("subCategory", subCategory);
  formData.append("taskName", taskName);
  formData.append("jobType", jobType);
  formData.append("sql", sql);
  formData.append("dbName", dbName);
  formData.append("mapping", mapping);
  formData.append("rowTest", rowTests);
  formData.append("fieldTest", fieldTests);
  formData.append("mappingTableData", JSON.stringify(mappingData));
  formData.append("delimiter", delimiter);
  return postFormData("/api/auth/compare/dbtofile/compare", formData);
};

export const dbToDbCompare = (
  jobId,
  suiteIdOne,
  suiteIdTwo,
  category,
  subCategory,
  taskName,
  jobType,
  sqlOne,
  sqlTwo,
  dbNameOne,
  dbNameTwo,
  mapping,
  rowTests,
  fieldTests,
  mappingData,
) => {
  const formData = new FormData();
  formData.append("jobId", jobId);
  formData.append("suiteIdOne", suiteIdOne);
  formData.append("suiteIdTwo", suiteIdTwo);
  formData.append("category", category);
  formData.append("subCategory", subCategory);
  formData.append("taskName", taskName);
  formData.append("jobType", jobType);
  formData.append("sqlOne", sqlOne);
  formData.append("sqlTwo", sqlTwo);
  formData.append("dbNameOne", dbNameOne);
  formData.append("dbNameTwo", dbNameTwo);
  formData.append("mapping", mapping);
  formData.append("rowTests", rowTests);
  formData.append("fieldTests", fieldTests);
  formData.append("mappingTableData", JSON.stringify(mappingData));
  return postFormData("/api/auth/compare/dbtodb", formData);
};

export const fileToSpecCompareMultiPart = (
  jobId,
  testSuiteName,
  suiteId,
  file,
  specName,
  category,
  subCategory,
  taskName,
  jobType,
) => {
  const formData = new FormData();
  formData.append("inputFile", file);
  formData.append("jobId", jobId);
  formData.append("specName", specName);
  formData.append("suiteId", suiteId);
  formData.append("category", category);
  formData.append("subCategory", subCategory);
  formData.append("taskName", taskName);
  formData.append("jobType", jobType);
  return postFormData(
    `/api/auth/compare/filetospec/${testSuiteName}`,
    formData,
  );
};

export const createJob = (
  taskType,
  taskCategory,
  taskSubCategory,
  status,
  createdBy,
) => {
  return request("POST", "/api/auth/job/create", {
    taskType,
    taskCategory,
    taskSubCategory,
    jobStatus: status,
    createdBy,
  });
};

export const updateJob = (jobId, jobStatus, error, runEnd) => {
  return request("POST", "/api/auth/job/update", {
    jobId,
    jobStatus,
    errorDescription: error,
    runEnd,
  });
};

export const getAllJobs = () => {
  return request("GET", "/api/auth/job/fetch/all");
};

export const createSuiteJob = (
  defaultEnvironment,
  runStart,
  runBy,
  runEnd,
  runStatus,
  msName,
  totalSteps,
) => {
  return request("POST", "/api/auth/job/create/suites", {
    defaultEnvironment,
    runStart,
    runBy,
    runEnd,
    runStatus,
    msName,
    totalSteps,
  });
};
